import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'
import {
  Button,
  Input,
  FormField,
  Card,
  LanguageSelector,
} from '../components'

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setUser, setOrganizations, setActiveOrg } = useAuthStore()

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim()

    try {
      await apiClient.post('/auth/send-link', { email })
      alert(t('auth.magicLinkSent', { email }))
    } catch (err) {
      console.error(err)
      alert(err instanceof Error ? err.message : t('auth.errors.networkError'))
    }
  }, [t])

  // Handle magic link token from URL
  const handleTokenSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const token = (form.elements.namedItem('token') as HTMLInputElement).value.trim()

    try {
      const data = await apiClient.get<{ user: { id: string; email: string; name: string; language?: string } }>(
        `/auth/verify?token=${encodeURIComponent(token)}`
      )
      setUser(data.user)

      // Fetch organizations (cookie is automatically sent)
      const orgData = await apiClient.get<{ organizations: { id: string; name: string; role?: string }[] }>('/organizations')
      const orgs = (orgData.organizations || []).map((o) => ({ ...o, role: o.role || 'member' }))
      setOrganizations(orgs)
      if (orgs.length > 0) setActiveOrg(orgs[0])
      navigate('/dashboard')
    } catch (err) {
      console.error(err)
      alert(err instanceof Error ? err.message : t('auth.errors.loginFailed'))
    }
  }, [navigate, setUser, setOrganizations, setActiveOrg, t])

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-indigo-700">📚 {t('app.name')}</h1>
          <p className="mt-2 text-gray-600">{t('app.tagline')}</p>
        </div>

        <div className="flex justify-end">
          <LanguageSelector variant="buttons" />
        </div>

        <Card padding="lg">
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('auth.login')}</h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="magic-link-form">
                <FormField>
                  <Input
                    name="email"
                    type="email"
                    required
                    placeholder={t('auth.email')}
                    data-testid="email-input"
                  />
                </FormField>
                <Button type="submit" size="lg" className="w-full" data-testid="send-magic-link-button">
                  {t('auth.sendMagicLink')}
                </Button>
              </form>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm text-gray-500 mb-3">{t('auth.enterToken')}</p>
              <form onSubmit={handleTokenSubmit} className="space-y-4" data-testid="token-form">
                <FormField>
                  <Input
                    name="token"
                    type="text"
                    placeholder="token..."
                    className="text-sm font-mono"
                    data-testid="token-input"
                  />
                </FormField>
                <Button type="submit" variant="secondary" size="lg" className="w-full" data-testid="login-with-token-button">
                  {t('auth.loginWithToken')}
                </Button>
              </form>
            </div>
          </div>
        </Card>

        <p className="text-center text-sm text-gray-500">
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="text-indigo-600 hover:underline">
            {t('auth.register')}
          </Link>
        </p>
      </div>
    </div>
  )
}
